<template lang="pug">
el-drawer.reject-return-drawer(
    :title="$t('Reject return')"
    :visible.sync="visibilityInternal"
    direction="rtl"
    size="40%"
    :wrapper-closable="false"
    @closed="reset"
)
    section.body(v-if="cancellation")
        p.hint {{ $t('State what you found at goods-in. The cause is forwarded to connected marketplaces, which use it to decide whether the customer is refunded.') }}

        el-form(
            ref="rejectForm"
            label-position="top"
            size="small"
            :model="form"
            :rules="rules"
        )
            el-form-item(:label="$t('Cause of rejection')" prop="rejection_cause")
                el-select(
                    v-model="form.rejection_cause"
                    :placeholder="$t('Please select')"
                    style="width: 100%"
                )
                    el-option(
                        v-for="cause in causeOptions"
                        :key="cause.key"
                        :label="cause.label"
                        :value="cause.key"
                    )

            el-form-item(:label="$t('Note to the customer')")
                el-input(
                    v-model="form.comment"
                    type="textarea"
                    :rows="3"
                    :placeholder="$t('Sole worn through, visual inspection by …')"
                    maxlength="1000"
                    show-word-limit
                )
                .help {{ $t('Optional detail shown to the customer in the rejection email, in addition to the cause above.') }}

    .drawer-footer
        .left
            el-button(
                size="small"
                icon="el-icon-fa-times fas"
                @click="visibilityInternal = false"
            ) {{ $t('Cancel') }}
        .right
            el-button(
                size="small"
                type="danger"
                icon="el-icon-fa-xmark fas"
                :disabled="!form.rejection_cause"
                :loading="saving"
                @click="submit"
            ) {{ $t('Reject') }}
</template>

<i18n>
{
    "en": {
        "Reject return": "Reject return",
        "Cause of rejection": "Cause of rejection",
        "State what you found at goods-in. The cause is forwarded to connected marketplaces, which use it to decide whether the customer is refunded.": "State what you found at goods-in. The cause is forwarded to connected marketplaces, which use it to decide whether the customer is refunded.",
        "Optional detail shown to the customer in the rejection email, in addition to the cause above.": "Optional detail shown to the customer in the rejection email, in addition to the cause above.",
        "Sole worn through, visual inspection by …": "Sole worn through, visual inspection by …",
        "Note to the customer": "Note to the customer",
        "returned-wrong-item": "Wrong item returned",
        "exchange-requested": "Exchange requested instead of withdrawal",
        "item-missing-in-parcel": "Item was not in the parcel",
        "third-party-item": "Third-party item (not from our range)",
        "hygiene-seal-broken": "Hygiene seal broken",
        "item-damaged-by-customer": "Item returned damaged",
        "return-period-exceeded": "Withdrawal period exceeded",
        "Return rejected": "Return rejected",
        "Rejection failed": "Rejection failed"
    },
    "de": {
        "Reject return": "Widerruf ablehnen",
        "Reject": "Ablehnen",
        "Cancel": "Abbrechen",
        "Please select": "Bitte wählen",
        "Cause of rejection": "Grund der Ablehnung",
        "State what you found at goods-in. The cause is forwarded to connected marketplaces, which use it to decide whether the customer is refunded.": "Geben Sie an, was Sie beim Wareneingang festgestellt haben. Der Grund wird an angebundene Marktplätze übermittelt und entscheidet dort über die Erstattung an den Kunden.",
        "Optional detail shown to the customer in the rejection email, in addition to the cause above.": "Optionale Ergänzung, die dem Kunden zusätzlich zum obigen Grund in der Ablehnungs-E-Mail angezeigt wird.",
        "Sole worn through, visual inspection by …": "Sohle abgelaufen, Sichtprüfung durch …",
        "Note to the customer": "Hinweis an den Kunden",
        "returned-wrong-item": "Falscher Artikel zurückgesendet",
        "exchange-requested": "Umtausch statt Widerruf",
        "item-missing-in-parcel": "Artikel lag dem Paket nicht bei",
        "third-party-item": "Fremdartikel (nicht aus unserem Sortiment)",
        "hygiene-seal-broken": "Hygienesiegel beschädigt",
        "item-damaged-by-customer": "Artikel beschädigt zurückgesendet",
        "return-period-exceeded": "Widerrufsfrist überschritten",
        "Return rejected": "Widerruf abgelehnt",
        "Rejection failed": "Ablehnung fehlgeschlagen"
    }
}
</i18n>

<script>
import { adminClient as apolloClient } from '@/store/apollo';
import TransitionCancellationGql from '@/extensions/shop/store/graphql/mutations/TransitionCancellation.gql';
import { REJECTION_CAUSES } from '@/extensions/shop/rejectionCauses';

export default {
    name: 'RejectReturnDrawer',
    props: {
        visibility: { type: Boolean, default: false },
        cancellation: { type: Object, default: null },
    },
    data: () => ({
        form: {
            rejection_cause: '',
            comment: '',
        },
        saving: false,
    }),
    computed: {
        visibilityInternal: {
            get() { return this.visibility; },
            set(value) { this.$emit('update:visibility', value); },
        },
        causeOptions() {
            return REJECTION_CAUSES.map(key => ({ key, label: this.$t(key) }));
        },
        // vee-validate v2 pipe-string rules — a rejection without a cause is
        // exactly the hole this feature closes, so the backend rejects it too.
        rules() {
            return {
                rejection_cause: 'required',
            };
        },
    },
    methods: {
        reset() {
            this.form = { rejection_cause: '', comment: '' };
            this.saving = false;
        },
        async submit() {
            if (!this.cancellation || !this.form.rejection_cause) return;
            this.saving = true;
            const variables = {
                id: parseInt(this.cancellation.id),
                status: 'rejected',
                rejection_reason: 'admin',
                rejection_cause: this.form.rejection_cause,
            };
            if (this.form.comment && this.form.comment.trim()) {
                variables.comment = this.form.comment.trim();
            }
            try {
                await apolloClient.mutate({
                    mutation: TransitionCancellationGql,
                    variables,
                });
                this.$notify.success({
                    title: this.$t('Return rejected'),
                    iconClass: 'el-icon-fa-xmark fas',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                this.$emit('success');
                this.visibilityInternal = false;
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('Rejection failed'),
                    message: err.message || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.reject-return-drawer
    ::v-deep .el-drawer__body
        display: flex
        flex-direction: column

.body
    flex: 1
    padding: 16px 20px
    overflow-y: auto

.hint
    color: var(--color-text-secondary, #606266)
    font-size: 13px
    line-height: 1.4
    margin-bottom: 16px

.help
    display: block
    margin-top: 4px
    font-size: 12px
    line-height: 1.4
    color: var(--color-text-muted, #909399)

.drawer-footer
    padding: 12px 20px
    border-top: 1px solid var(--border-color, #ebeef5)
    display: flex
    align-items: center
    justify-content: space-between
</style>
